import { computePosition, flip, shift, offset, arrow, Middleware } from "@floating-ui/dom";
import { Placement } from "@floating-ui/core";
import { parseIntOrNull } from "../utils/number.js";

/** Attributes:
 * selector (string) - Specify a CSS selector for target element for this tooltip.
 * styles (bool) - If present, default styles are added.
 * placement (string) - Specify where the default placement should be. Base: top, right, bottom, left. Mods: -start, -end.
 * offset (number) - Specify a offset (distance) from trigger element to tooltip container in pixels.
 */
export class CmsTooltip extends HTMLElement {
    defaultOptions: ITooltipOptions = {
        placement: TooltipPlacement.Bottom,
        autoAlign: true,
        offsetFromTrigger: 6,
        clickOpen: false,
        triggerSelector: null,
        addDefaultStyles: true
    };

    options: ITooltipOptions;

    _visible: boolean = true;
    get visible(){
        return this._visible;
    }
    set visible(val:boolean){
        this._visible = val;
    }

    defaultStyles = `
        h1, h2, h3, h4, h5 { margin: 10px 0 5px; }
        .container {
            display: none;
            position: absolute;
            border-radius: 3px;
            padding: 10px;
            background-color: #000;
            color: #fff;
            box-shadow: rgb(0 0 0 / 40%) 0 0 5px;
            pointer-events: none;
        }
        p{
            margin: 0 0 10px;
        }

        #arrow {
            content: '';
            position: absolute;
            pointer-events: none;
            border: 8px solid transparent;
        }
        #arrow.top{
            border-top: none;
            border-bottom-color: #000;
            filter: drop-shadow(0 -0.0625rem 0.0625rem rgba(0 0 0 / 20%));
        }
        #arrow.bottom{
            border-bottom: none;
            border-top-color: #000;
            filter: drop-shadow(0 0.0625rem 0.0625rem rgba(0 0 0 / 20%));
        }
        #arrow.right{
            border-right: none;
            border-left-color: #000;
            filter: drop-shadow(0.1rem 0 0.0625rem rgba(0 0 0 / 20%));
        }
        #arrow.left{
            border-left: none;
            border-right-color: #000;
            filter: drop-shadow(-0.1rem 0 0.0625rem rgba(0 0 0 / 20%));
        }
    `;

    container: HTMLDivElement;
    trigger: HTMLElement;
    arrow: HTMLElement;

    constructor(options?: ITooltipOptions) {
        super();

        if (options) {
            this.options = { ...this.defaultOptions, ...options };
        }

        this.setupElements();
    }

    setupElements() {
        // Attach shadow
        this.attachShadow({ mode: "open" });

        // Create the main container
        this.container = document.createElement("div");
        this.container.classList.add("container");

        // Move all children to the container
        if (this.options && this.options.bodyTemplate){
            this.container.appendChild(this.options.bodyTemplate);
        } else {
            while(this.firstChild) {
                this.container.appendChild(this.firstChild);
            }
        }

        this.shadowRoot.appendChild(this.container);

        // Add arrow
        this.arrow = document.createElement("div");
        this.arrow.id = "arrow";
        this.container.appendChild(this.arrow);
    }

    addStyles() {
        if (!this.options.addDefaultStyles) {
            return;
        }

        const styles = document.createElement("style");
        styles.innerHTML = this.defaultStyles;
        this.shadowRoot.appendChild(styles);
    }

    connectedCallback() {
        if (!this.options) {
            this.readOptionsFromAttributes();
        }

        this.addStyles();
        this.setTrigger();
        this.setPosition();
        this.attachEventHandlers();
    }

    setTrigger() {
        const context = this.options.context ? this.options.context : document;
        if (this.options.triggerSelector) {
            this.trigger = context.querySelector(this.options.triggerSelector) as HTMLElement;
        } else {
            // Use sibling above
            this.trigger = this.previousElementSibling as HTMLElement;
        }
    }

    setPosition() {
        const placement = this.getFloatingUiPlacementFromOptions();
        const middleware = this.createMiddleware();

        computePosition(this.trigger, this.container, {
            placement,
            middleware
        }).then((comp) => {
            this.container.style.left = comp.x + "px";
            this.container.style.top = comp.y + "px";

            const {x: arrowX, y: arrowY} = comp.middlewareData.arrow;
            const arrowSide = this.getFloatingUiArrowSideFromPlacement(comp.placement);

            this.arrow.removeAttribute("class");
            this.arrow.classList.add(arrowSide);

            Object.assign(this.arrow.style, {
                left: arrowX != null ? `${arrowX}px` : '',
                top: arrowY != null ? `${arrowY}px` : '',
                right: '',
                bottom: '',
                [arrowSide.toString()]: '-8px',
            });
        });
    }

    createMiddleware(): Middleware[] {
        const middleware = [offset(this.options.offsetFromTrigger)];

        if (this.options.autoAlign) {
            middleware.push(
                flip(),
                shift({padding: 5})
            );
        }

        middleware.push(arrow({element: this.arrow}));

        return middleware;
    }

    attachEventHandlers() {
        this.trigger.addEventListener("mouseenter", this.showTooltip.bind(this));
        this.trigger.addEventListener("mouseleave", this.hideTooltip.bind(this));
        this.trigger.addEventListener("focus", this.showTooltip.bind(this));
        this.trigger.addEventListener("blur", this.hideTooltip.bind(this));
        window.addEventListener('scroll', this.setPosition.bind(this));
        window.addEventListener('resize', this.setPosition.bind(this));
    }

    showTooltip() {
        if (this._visible === true) {
            this.container.style.display = 'block';
            this.setPosition();
        }
    }

    hideTooltip() {
        this.container.style.display = '';
    }

    getFloatingUiPlacementFromOptions(): Placement {
        return (this.options.placement?.toString() || "bottom") as Placement;
    }

    getFloatingUiArrowSideFromPlacement(placement: string): Placement {
        return {
            top: 'bottom',
            right: 'left',
            bottom: 'top',
            left: 'right',
        }[placement.split('-')[0]] as Placement;
    }

    readOptionsFromAttributes() {
        this.options = {...this.defaultOptions};

        const triggerSelector = this.getAttribute("selector");
        if (triggerSelector && triggerSelector.length) {
            this.options.triggerSelector = triggerSelector;
        }

        const styles = this.hasAttribute("styles");
        this.options.addDefaultStyles = styles;

        const placement = this.getAttribute("placement");
        if (placement && placement.length) {
            this.options.placement = placement as TooltipPlacement;
        }

        const offset = this.getAttribute("offset");
        if (offset && offset.length) {
            this.options.offsetFromTrigger = parseIntOrNull(offset) || 6;
        }

        if (this.hasAttribute("visible")) {
            const visible = this.getAttribute("visible");
            if (visible && visible.length && (visible.toLowerCase() === "true" || visible === "1")) {
                this._visible = true;
            } else {
                this._visible = false;
            }
        }
    }
}

interface ITooltipOptions {
    placement?: TooltipPlacement;
    autoAlign?: boolean;
    offsetFromTrigger?: number;
    clickOpen?: boolean;
    triggerSelector?: string;
    addDefaultStyles?: boolean;
    context?: HTMLElement;
    bodyTemplate?: HTMLElement;
}

export enum TooltipPlacement {
    Top = "top",
    TopStart = "top-start",
    TopEnd = "top-end",
    Right = "right",
    RightStart = "right-start",
    RightEnd = "right-end",
    Bottom = "bottom",
    BottomStart = "bottom-start",
    BottomEnd = "bottom-end",
    Left = "left",
    LeftStart = "left-start",
    LeftEnd = "left-end"
}

customElements.define('cms-tooltip', CmsTooltip);